import { useState, useEffect } from "react";

// Module-scoped map of in-flight fetch promises, keyed by URL.
// Decouples fetch lifecycle from React component lifecycle so that
// StrictMode's simulated unmount–remount doesn't duplicate or abort requests.
const inflight = new Map();

const fetchSignupData = (url) => {
  if (inflight.has(url)) return inflight.get(url);

  const promise = fetch(url)
    .then((resp) => {
      if (!resp.ok) throw new Error(`Signup request failed with status ${resp.status}`);
      return resp.json();
    })
    .finally(() => inflight.delete(url));

  inflight.set(url, promise);
  return promise;
};

const useClientAppSignupRequest = (url) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signupRequest, setSignupRequest] = useState(null);
  const [signupsEnabled, setSignupsEnabled] = useState(null);
  const [signupsDisabledReason, setSignupsDisabledReason] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetchSignupData(url)
      .then((data) => {
        if (cancelled) return;

        if (!!data.signupRequestValid && !!data.signupsEnabled) {
          setSignupRequest(data.signupRequest);
          setSignupsEnabled(true);
        } else if (data.signupsEnabled === false) {
          setSignupsEnabled(false);
          setSignupsDisabledReason(data.signupsDisabledReason);
        } else if (data.signupRequestError === "expired") {
          setError("Your signup link has expired.");
        } else if (data.signupRequestError === "submitted") {
          setError("It looks like you’ve already signed up.");
          setSubmitted(true);
        } else {
          setError("Failed to load signup details.");
        }
      })
      .catch((err) => {
        console.error("Failed to load signup details", err);
        if (cancelled) return;
        setError("Failed to load signup details.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { loading, error, signupRequest, signupsEnabled, signupsDisabledReason, submitted };
};

export default useClientAppSignupRequest;
